import { useEffect, type ComponentType, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@eon/utils';

export interface SidebarItem {
  to: string;
  label: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  badge?: ReactNode;
}

export interface SidebarSection {
  label: string;
  items: SidebarItem[];
}

export interface SidebarProps {
  /** Flat list of items (legacy) — OR use `sections` for grouped layout */
  items?: SidebarItem[];
  /** Grouped sections with labels */
  sections?: SidebarSection[];
  brand: ReactNode;
  /**
   * Optional brand glyph rendered to the left of `brand`. When omitted, the
   * sidebar falls back to the legacy ⚡ badge so existing call-sites that
   * haven't been migrated to a real logo still look intentional.
   */
  brandLogo?: ReactNode;
  active: string;
  onNavigate: (to: string) => void;
  footer?: ReactNode;
  className?: string;
  /**
   * Mobile drawer open state. Below the `lg` breakpoint the sidebar renders
   * as an off-canvas drawer controlled by this flag; on `lg+` it is always
   * visible and this flag has no visual effect.
   */
  mobileOpen?: boolean;
  /** Called when the mobile drawer requests to close (backdrop tap, ✕, Esc, navigation). */
  onMobileClose?: () => void;
}

function NavButton({
  item,
  isActive,
  onNavigate,
}: {
  item: SidebarItem;
  isActive: boolean;
  onNavigate: (to: string) => void;
}) {
  const Icon = item.icon;
  return (
    <button
      key={item.to}
      type="button"
      onClick={() => onNavigate(item.to)}
      className={cn(
        'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-primary/10 text-primary shadow-glow'
          : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
      )}
    >
      {/* Active left accent bar */}
      {isActive && (
        <span className="bg-primary absolute left-0 h-5 w-0.5 rounded-r-full" />
      )}
      {Icon ? (
        <Icon
          className={cn(
            'h-4 w-4 shrink-0 transition-colors',
            isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground',
          )}
        />
      ) : null}
      <span className="truncate">{item.label}</span>
      {item.badge ? <span className="ml-auto">{item.badge}</span> : null}
    </button>
  );
}

/**
 * Premium glassmorphism sidebar.
 * Supports both flat `items` (backward-compat) and grouped `sections`.
 */
export function Sidebar({
  brand,
  brandLogo,
  items,
  sections,
  active,
  onNavigate,
  footer,
  className,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  // Close the mobile drawer with Esc for accessibility.
  useEffect(() => {
    if (!mobileOpen || !onMobileClose) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMobileClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileOpen, onMobileClose]);

  const handleNavigate = (to: string) => {
    onNavigate(to);
    // Navigating from the drawer should dismiss it on mobile.
    onMobileClose?.();
  };

  return (
    <>
      {/* Mobile backdrop — only rendered below lg while the drawer is open */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          aria-hidden="true"
          onClick={onMobileClose}
        />
      ) : null}
      <aside
        className={cn(
          'flex h-full shrink-0 flex-col border-r',
          'bg-[hsl(var(--glass-bg))] backdrop-blur-xl border-[hsl(var(--glass-border))]',
          // Off-canvas drawer below lg; static column on lg+ (original behaviour).
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out lg:static lg:z-auto lg:translate-x-0 lg:transition-none',
          mobileOpen ? 'translate-x-0 shadow-2xl lg:shadow-none' : '-translate-x-full',
          className,
        )}
        style={{ width: 'var(--sidebar-width, 15rem)' }}
      >
      {/* Brand area */}
      <div className="flex h-16 items-center gap-3 border-b border-[hsl(var(--glass-border))] px-5">
        {brandLogo ?? (
          <div className="relative grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
            <span className="font-display text-sm font-bold text-primary">⚡</span>
            {/* Subtle pulse ring */}
            <span className="absolute inset-0 animate-glow rounded-xl opacity-60" />
          </div>
        )}
        <span className="font-display truncate text-sm font-semibold tracking-tight text-foreground">
          {brand}
        </span>
        {onMobileClose ? (
          <button
            type="button"
            onClick={onMobileClose}
            className="text-muted-foreground hover:text-foreground ml-auto rounded-lg p-1.5 transition-colors lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Navigation */}
      <nav className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {sections
          ? sections.map((section) => (
              <div key={section.label} className="mb-4">
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
                  {section.label}
                </p>
                {section.items.map((item) => (
                  <NavButton
                    key={item.to}
                    item={item}
                    isActive={item.to === active}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            ))
          : items?.map((item) => (
              <NavButton
                key={item.to}
                item={item}
                isActive={item.to === active}
                onNavigate={handleNavigate}
              />
            ))}
      </nav>

      {footer ? (
        <div className="border-t border-[hsl(var(--glass-border))] p-4">{footer}</div>
      ) : null}
      </aside>
    </>
  );
}
